/**
 * Pagina WhatsApi - Documentacao para Desenvolvedores (TASK-2307).
 *
 * Tabs URL-driven (?tab=<slug>), permission gate via whatsapi:read,
 * hash-scroll para âncoras em-pagina, e conteudo renderizado via
 * MarkdownContent.
 */

import 'highlight.js/styles/github-dark.css'

import { useEffect } from 'react'
import { Alert, Tabs } from '@mantine/core'
import { IconLock } from '@tabler/icons-react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { MarkdownContent } from '@/components/whatsapi/MarkdownContent'
import { useCan } from '@/store/auth'

// ─── Tab definitions ─────────────────────────────────────────────────────────

export const TABS = ['overview', 'quickstart', 'api', 'errors', 'examples', 'faq'] as const

export type DocTab = (typeof TABS)[number]

export const LABELS: Record<DocTab, string> = {
  overview:   'Visão geral',
  quickstart: 'Quickstart',
  api:        'API',
  errors:     'Erros',
  examples:   'Exemplos',
  faq:        'FAQ',
}

export function isDocTab(value: string): value is DocTab {
  return (TABS as readonly string[]).includes(value)
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function WhatsApiDocs() {
  const can = useCan()
  const allowed = can('whatsapi', 'read')

  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()

  const tabParam = searchParams.get('tab') ?? ''
  const active: DocTab = isDocTab(tabParam) ? tabParam : 'overview'

  // Redirect invalid slugs to overview
  useEffect(() => {
    if (tabParam && !isDocTab(tabParam)) {
      setSearchParams({ tab: 'overview' }, { replace: true })
    }
  }, [tabParam, setSearchParams])

  // Hash-scroll: fires when active tab changes OR hash changes
  useEffect(() => {
    const hash = location.hash || window.location.hash
    if (!hash) return
    const raf = requestAnimationFrame(() => {
      const el = document.getElementById(hash.slice(1))
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
    return () => cancelAnimationFrame(raf)
  }, [active, location.hash])

  if (!allowed) {
    return (
      <Alert icon={<IconLock size={16} />} color="yellow" variant="light">
        Você não tem permissão para visualizar a documentação do WhatsApi.
      </Alert>
    )
  }

  function handleTabChange(value: string | null) {
    if (value && isDocTab(value)) {
      setSearchParams({ tab: value })
    }
  }

  return (
    <Tabs value={active} onChange={handleTabChange}>
      <Tabs.List>
        {TABS.map((slug) => (
          <Tabs.Tab key={slug} value={slug}>
            {LABELS[slug]}
          </Tabs.Tab>
        ))}
      </Tabs.List>

      {TABS.map((slug) => (
        <Tabs.Panel key={slug} value={slug} pt="md">
          <MarkdownContent slug={slug} />
        </Tabs.Panel>
      ))}
    </Tabs>
  )
}
